import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import './pin-keypad';
import type { PinKeypad } from './pin-keypad';
import { ApiFactory } from '../api/easypos-api';
import { UserProfile } from '../datatypes/user-profile';
import { getOrCreateDeviceId } from '../datatypes/device';

// Full-viewport, non-dismissible lock screen shown when the register is
// manually locked. The register's token has already been cleared, so this
// re-authenticates by PIN via the anonymous auth/pin-unlock endpoint:
//   - the same cashier's PIN resumes their session in place;
//   - a different PIN does NOT hand the shift over (the architect's
//     @LOCK-OPEN-002 correction); the shift, its float and its drawer stay with
//     whoever opened them.
// On success it dispatches the existing `user-profile-set` window event, so
// app-index runs the full post-login flow (device gate, shop, realtime).
// A password fallback (`cancel-lock`) covers forgot-PIN / no-PIN shops so a
// locked register is never trapped.
//
// PIN ENTRY itself lives in <pin-keypad>, shared with the till's "who is
// serving" bar. This screen keeps only what is specific to unlocking: the
// endpoint it calls, what a success means, and the password escape hatch.
//
// NOTE for anyone extending this: unlocking is still AUTHENTICATION - it ends
// by issuing a token - so an internal staff record (a synthetic staff-{ulid}
// with no Login) cannot unlock. That is why the till's attribution bar is a
// separate path and not an extension of this one.
@customElement('register-lock-screen')
export class RegisterLockScreen extends LitElement {
  // Shown for context only ("Locked" + who locked it + which shop).
  @property({ type: String }) cashierName = '';
  @property({ type: String }) shopLabel = '';

  @state() private _busy = false;

  @query('pin-keypad') private _keypad!: PinKeypad | null;

  private _deviceId: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    // Lit's lifecycle hook is void-returning, so the device-id read is kicked
    // off rather than awaited. _unlock re-checks _deviceId and refuses with a
    // clear message if it has not landed, so an unlock can never race ahead of
    // it - the operator is never silently unlocked against no device.
    void this._loadDeviceId();
  }

  private async _loadDeviceId() {
    this._deviceId = await getOrCreateDeviceId();
  }

  // Hand focus to the pad as soon as the screen paints so a counter numpad
  // works without clicking first. Nothing else here is focusable and the screen
  // is non-dismissible, so claiming focus cannot steal it from a live task.
  firstUpdated() {
    this._keypad?.focusPad();
  }

  private async _unlock(e: CustomEvent<{ pin: string }>) {
    const pad = this._keypad;
    if (this._busy || !pad) return;
    if (!this._deviceId) {
      pad.reject('This device is not set up. Sign in with email + password.');
      return;
    }
    this._busy = true;
    const result = await ApiFactory.anonymous().pinUnlock(
      this._deviceId,
      e.detail.pin
    );
    this._busy = false;

    if (result.success && result.data) {
      const t = result.data;
      const profile = new UserProfile({
        id: t.id,
        displayName: t.displayName,
        emailAddress: t.emailAddress ?? '',
        authProvider: 'internal',
        accessToken: t,
      });
      // app-index's window listener runs the full post-login flow and
      // clears the lock. Do not clear the pad here - the screen unmounts.
      window.dispatchEvent(
        new CustomEvent<UserProfile>('user-profile-set', { detail: profile })
      );
      return;
    }

    // Failure. A 429 carries the remaining lockout; everything else is a plain
    // rejection. The pad owns the countdown and the shake.
    pad.reject(
      result.error ?? 'Incorrect PIN.',
      result.status === 429 ? result.lockedSeconds : undefined
    );
  }

  private _usePassword() {
    this.dispatchEvent(
      new CustomEvent('cancel-lock', { bubbles: true, composed: true })
    );
  }

  render() {
    return html`
      <div class="wrap">
        <div class="card">
          <sl-icon name="lock-fill" class="lock-icon"></sl-icon>
          <h1>Register locked</h1>
          <p class="sub">
            ${
              this.cashierName
                ? html`Locked by <strong>${this.cashierName}</strong>. `
                : nothing
            }Enter
            a staff PIN to
            unlock${
              this.shopLabel
                ? html` <span class="shop">${this.shopLabel}</span>`
                : nothing
            }.
          </p>

          <pin-keypad
            submit-label="Unlock"
            .busy=${this._busy}
            @pin-submit=${this._unlock}
          ></pin-keypad>

          <button class="link" @click=${this._usePassword}>
            Use email + password instead
          </button>
        </div>
      </div>
    `;
  }

  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 3000;
      display: block;
    }
    .wrap {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: linear-gradient(160deg, #1a2b47, #0f1a2e);
    }
    .card {
      width: min(360px, 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      color: #eef2f8;
    }
    /* The dark palette for the shared pad. Without these it would render in the
       page's ordinary light surface colours, which are unreadable here. */
    pin-keypad {
      width: 100%;
      --pin-keypad-color: #eef2f8;
      --pin-keypad-muted: #b8c4d6;
      --pin-keypad-accent: #d05f27;
      --pin-keypad-key-bg: rgba(255, 255, 255, 0.06);
      --pin-keypad-key-border: rgba(255, 255, 255, 0.14);
      --pin-keypad-key-hover-bg: rgba(255, 255, 255, 0.12);
      --pin-keypad-dot-border: #5b6d88;
      --pin-keypad-error: #ff9a8b;
      --pin-keypad-warning: #ffcf8b;
    }
    .lock-icon {
      font-size: 34px;
      color: #d05f27;
      margin-bottom: 8px;
    }
    h1 {
      margin: 0 0 4px;
      font-size: 22px;
      font-weight: 700;
    }
    /* The lock screen keeps its own subtitle rather than using <pin-keypad>'s
       plain-text hint, because it emphasises WHO locked it and WHICH shop - the
       two things an operator checks before typing a PIN into a strange tablet.
       The pad has no business knowing that. */
    .sub {
      margin: 0 0 18px;
      font-size: 13px;
      color: #b8c4d6;
      line-height: 1.4;
    }
    .shop {
      color: #eef2f8;
      font-weight: 600;
    }
    .link {
      appearance: none;
      background: none;
      border: none;
      color: #9fb0c6;
      font-size: 12.5px;
      margin-top: 14px;
      cursor: pointer;
      text-decoration: underline;
    }
    .link:hover {
      color: #eef2f8;
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'register-lock-screen': RegisterLockScreen;
  }
}
